import { Box } from '@mui/material';
import { SyntheticEvent, useCallback, useState } from 'react';
import { TaskFilters, TasksFilters } from './components/TasksFilters';
import { TasksKanban } from './components/TasksKanban/TasksKanban';
import { TasksStatistics } from './components/TasksStatistics';
import { TasksTable } from './components/TasksTable';
import { useTasksData } from './hooks/useTasksData';

type MainProps = {
  clientScope?: {
    clientId?: string;
    clientName?: string;
  };
  teamScope?: {
    teamId?: string;
    teamName?: string;
  };
  newTaskState?: any;
};

export const enum LayoutType {
  LIST = 'list',
  KANBAN = 'kanban',
}

export const TasksMain = ({ clientScope, teamScope }: MainProps) => {
  const currentRoleViewType =
    (sessionStorage.getItem('currentRoleViewType') as LayoutType) || LayoutType.KANBAN;
  const isClientScoped = Boolean(clientScope?.clientId || clientScope?.clientName);
  const isTeamScoped = Boolean(teamScope?.teamId || teamScope?.teamName);

  const [layout, setLayout] = useState<LayoutType>(currentRoleViewType);
  const [tabValue, setTabValue] = useState<number>(isClientScoped || isTeamScoped ? 1 : 0);
  const [filters, setFilters] = useState<TaskFilters>({
    priority: [],
    time: [],
    client: [clientScope?.clientName || ''],
    recordTypes: [],
    search: '',
    needAttention: false,
    ...(isTeamScoped
      ? {
          assignedTo: [teamScope?.teamName || ''],
        }
      : {}),
  });

  const { tasksData, loading, refetch } = useTasksData(tabValue === 0, filters);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statisticsRevision, setStatisticsRevision] = useState(0);

  const bumpStatistics = useCallback(() => {
    setStatisticsRevision((n) => n + 1);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
    setRefreshTrigger((prev) => prev + 1);
    bumpStatistics();
  }, [refetch, bumpStatistics]);

  const handleLayoutChange = (_: SyntheticEvent, newValue: LayoutType) => {
    sessionStorage.setItem('currentRoleViewType', newValue);
    setLayout(newValue);
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const handleApplyFilters = (newFilters: TaskFilters) => {
    setFilters(newFilters);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        margin: '24px',
        gap: '24px',
      }}
    >
      {!isClientScoped && !isTeamScoped && (
        <TasksStatistics
          mine={tabValue === 0}
          filters={filters}
          statisticsRevision={statisticsRevision}
        />
      )}
      <TasksFilters
        onLayoutChange={handleLayoutChange}
        layout={layout}
        onTabChange={handleTabChange}
        tabValue={tabValue}
        onApplyFilters={handleApplyFilters}
        onRefresh={handleRefresh}
        newTaskState={
          isClientScoped
            ? {
                presetClient: {
                  id: clientScope?.clientId,
                  name: clientScope?.clientName,
                },
              }
            : isTeamScoped
              ? {
                  presetTeam: {
                    id: teamScope?.teamId,
                    name: teamScope?.teamName,
                  },
                }
              : undefined
        }
      />
      {layout === LayoutType.KANBAN ? (
        <TasksKanban
          mine={tabValue === 0}
          filters={filters}
          tasksData={tasksData}
          loading={loading}
          onRefresh={handleRefresh}
          onStatisticsInvalidate={bumpStatistics}
          subSections={isClientScoped || isTeamScoped}
          tabValue={tabValue}
        />
      ) : (
        <TasksTable
          mine={tabValue === 0}
          filters={filters}
          refreshTrigger={refreshTrigger}
          onStatisticsInvalidate={bumpStatistics}
          tabValue={tabValue}
        />
      )}
    </Box>
  );
};
